import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Loader2, Wallet, TrendingUp, Award, Users, RefreshCw, Percent, Hourglass } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sublabel, accent = "#0A2540", testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#CBD5E1] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
        <p className="text-xs text-[#64748B] uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid={`${testid}-value`}>{value}</p>
      {sublabel && <p className="text-[11px] text-[#64748B] mt-1">{sublabel}</p>}
    </div>
  );
}

export default function AdminTFTStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tft/stats");
      setStats(data);
    } catch (e) {
      toast.error("Erreur de chargement", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const monthLabel = stats ? new Date(stats.month_start).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "";

  return (
    <div className="space-y-5" data-testid="admin-tft-stats-panel">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-5 h-5 text-[#FACC15]" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Statistiques TFT</h2>
          </div>
          <p className="text-sm text-white/70">Données en temps réel — {monthLabel || "ce mois"}. Taux de cashback actuel : <strong className="text-[#FACC15]">{stats ? `${(stats.cashback_rate * 100).toFixed(1)}%` : "—"}</strong></p>
        </div>
        <Button onClick={load} size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="reload-tft-stats-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {loading || !stats ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#0A2540] animate-spin" /></div>
      ) : (
        <>
          {/* KPIs grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              icon={Percent}
              label="Cashback ce mois"
              value={`${stats.cashback_this_month.toLocaleString()} TFT`}
              sublabel={`${stats.cashback_count_this_month} versements · ${stats.cashback_alltime.toLocaleString()} TFT cumulés`}
              accent="#FACC15"
              testid="stat-cashback-month"
            />
            <StatCard
              icon={TrendingUp}
              label="Recettes TFT ce mois"
              value={`${stats.tft_revenue_xaf_this_month.toLocaleString()} XAF`}
              sublabel={`${stats.tft_bookings_this_month} réservations payées en TFT`}
              accent="#10B981"
              testid="stat-tft-revenue"
            />
            <StatCard
              icon={Users}
              label="Adoption portefeuille"
              value={`${stats.wallet_adoption_rate}%`}
              sublabel={`${stats.wallet_adopters} / ${stats.total_passengers} passagers (solde > 0 ou paiement TFT)`}
              accent="#3B82F6"
              testid="stat-wallet-adoption"
            />
            <StatCard
              icon={Hourglass}
              label="Packs vendus ce mois"
              value={`${stats.pack_purchases_approved_this_month}`}
              sublabel={`${stats.pack_bookings_this_month} réservations utilisant un pack`}
              accent="#F59E0B"
              testid="stat-pack-sales"
            />
          </div>

          {/* Cashback recommendation */}
          <div className="bg-white rounded-2xl border border-[#CBD5E1] p-4">
            <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide mb-2" style={{ fontFamily: 'Outfit' }}>Recommandation cashback</h3>
            {stats.tft_bookings_this_month === 0 ? (
              <p className="text-xs text-[#64748B]">Pas encore assez de données. Promouvez le portefeuille TFT auprès des passagers pour collecter des signaux d&apos;ajustement.</p>
            ) : stats.wallet_adoption_rate < 10 ? (
              <p className="text-xs text-[#0A2540]">
                Adoption faible (&lt; 10%). <strong>Envisagez de relever le cashback à 3%</strong> pendant 1 mois pour stimuler l&apos;adoption.
                <span className="text-[#64748B]"> Impact estimé : +{Math.round(stats.cashback_this_month * 0.5).toLocaleString()} TFT/mois.</span>
              </p>
            ) : stats.wallet_adoption_rate > 40 ? (
              <p className="text-xs text-[#0A2540]">
                Adoption élevée (&gt; 40%). Vous pourriez <strong>réduire à 1.5%</strong> pour économiser sans nuire à l&apos;usage.
                <span className="text-[#64748B]"> Économie estimée : -{Math.round(stats.cashback_this_month * 0.25).toLocaleString()} TFT/mois.</span>
              </p>
            ) : (
              <p className="text-xs text-[#0A2540]">Adoption équilibrée. Le taux actuel de <strong>{(stats.cashback_rate * 100).toFixed(1)}%</strong> semble bien calibré. Surveillez l&apos;évolution.</p>
            )}
          </div>

          {/* Top TFT payers */}
          <div className="bg-white rounded-2xl border border-[#CBD5E1] p-4" data-testid="top-tft-payers-section">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-[#FACC15]" />
              <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Top passagers TFT (ce mois)</h3>
            </div>
            {stats.top_tft_payers.length === 0 ? (
              <p className="text-xs text-[#64748B] text-center py-4">Aucun paiement TFT enregistré ce mois.</p>
            ) : (
              <div className="space-y-2">
                {stats.top_tft_payers.map((p, i) => (
                  <div key={p.user_id} className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded-lg" data-testid={`top-tft-payer-${i}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-[#FACC15] text-[#0A2540]" : i === 1 ? "bg-[#CBD5E1] text-[#0A2540]" : i === 2 ? "bg-[#F59E0B]/30 text-[#0A2540]" : "bg-[#0A2540]/10 text-[#64748B]"}`}>#{i + 1}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0A2540] truncate">{p.name}</p>
                        <p className="text-[10px] text-[#64748B]">{p.bookings} réservation{p.bookings > 1 ? "s" : ""}</p>
                      </div>
                    </div>
                    <p className="text-sm font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid={`top-tft-amount-${i}`}>
                      {p.total_tft.toLocaleString()} <span className="text-[10px] font-normal text-[#64748B]">TFT</span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
